import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useAttachedNewCardMutation } from '@/store/services/paymentMethod';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

type CheckoutFormProps = {
	setStripeDialogOpen: (open: boolean) => void;
};

function CheckoutForm({ setStripeDialogOpen }: CheckoutFormProps) {
	const [attachedCard, { isLoading: attachedCardLoading }] = useAttachedNewCardMutation();
	const stripe = useStripe();
	const elements = useElements();
	const [loading, setLoading] = useState(false);
	const [message, setMessage] = useState('');

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!stripe || !elements) {
			setMessage('Stripe has not loaded yet.');
			return;
		}

		setLoading(true);
		setMessage('');

		const cardElement = elements.getElement(CardElement);

		if (!cardElement) {
			setMessage('Card details are not available.');
			setLoading(false);
			return;
		}

		const { error, paymentMethod } = await stripe.createPaymentMethod({
			type: 'card',
			card: cardElement
		});

		if (error) {
			setMessage(error.message || 'An unknown error occurred.');
			setLoading(false);
			return;
		}

		try {
			await attachedCard({ paymentMethodId: paymentMethod.id }).unwrap();
			setStripeDialogOpen(false);
		} catch (errors) {
			const typedError = errors;
			enqueueSnackbar(typedError?.data?.errors?.[0]?.detail || 'An error occurred', {
				variant: 'error'
			});
		}
		setLoading(false);
	};

	const cardElementOptions = {
		style: {
			base: {
				color: '#32325d',
				fontSize: '16px',
				fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
				fontSmoothing: 'antialiased',
				border: '1px solid #ccc',
				padding: '10px'
			},
			invalid: {
				color: '#e54242',
				iconColor: '#e54242'
			}
		}
	};

	return (
		<div className="checkout-container">
			<form
				onSubmit={handleSubmit}
				className="checkout-form"
			>
				<Typography
					variant="h6"
					className="mb-5 pt-4 text-center font-semibold text-1xl"
				>
					Add New Payment Method
				</Typography>
				<Box
					className="card-element-container"
					style={{ border: '1px solid #ccc', padding: '10px' }}
				>
					<CardElement
						className="card-element"
						options={cardElementOptions}
					/>
				</Box>
				{message && (
					<Box
						className="message"
						style={{ color: 'red', marginBottom: '10px', marginTop: '10px' }}
					>
						{message}
					</Box>
				)}
				<Button
					variant="contained"
					color="secondary"
					type="submit"
					disabled={!stripe || loading || attachedCardLoading}
					className="submit-btn w-full mt-5"
					startIcon={
						(loading || attachedCardLoading) && (
							<CircularProgress
								size={16}
								color="inherit"
							/>
						)
					}
				>
					Add Card
				</Button>
			</form>
		</div>
	);
}

export default CheckoutForm;
